import type moment from 'moment'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import { trackAddUnassignedEvent } from 'modules/shift_management/lib/trackEvents'
import UnassignedShiftsApi from 'modules/shift_management/store/api/collections/unassignedShifts'
import UnassignedShiftProp, {
  UnassignedShiftPayload,
} from 'modules/shift_management/store/internal/models/UnassignedShift'

import buildAddUnassignedShiftsParameters, {
  UnassignedShiftParam,
} from '../lib/buildAddUnassignedShiftParameters'
import { useSchedule } from './useSchedule'
import { useUnassignedShifts } from './useUnassignedShifts'

export interface Props {
  startDate: moment.Moment
  endDate: moment.Moment
  staffRequirements: Array<{
    job_catalog_roles_ids: number[]
    staff_count: number
  }>
  forms: UnassignedShiftForm[]
  onSuccess?: () => void
}

const useAddUnassignedDefaultShifts = ({
  startDate,
  endDate,
  staffRequirements,
  forms,
  onSuccess,
}: Props) => {
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUnassignedShifts] = useUnassignedShifts()

  const buildParameters = (): UnassignedShiftParam[] => {
    const unassignedShiftParams: UnassignedShiftParam[] = []
    let currentDate = startDate

    while (currentDate <= endDate) {
      forms.flatMap((form: UnassignedShiftForm) => {
        staffRequirements.forEach((requirement) => {
          unassignedShiftParams.push(
            ...buildAddUnassignedShiftsParameters({
              date: currentDate,
              unparsedStartTime: form.get('start_at').value,
              unparsedEndTime: form.get('end_at').value,
              defaultShiftId: +form.get('id').value,
              defaultShiftTitle: form.get('title').value,
              extraHours: form.get('extra_hours').value,
              notes: form.get('notes').value,
              locationId: +form.get('location_id').value,
              workAreaId: +form.get('work_area_id').value,
              color: form.get('color').value,
              counter: +requirement.staff_count,
              jobCatalogRolesIds: [...requirement.job_catalog_roles_ids],
              overnightShift: form.get('overnight_shift').value,
              allCompany: form.get('all_company').value,
              teamsIdsVisibilities: form.get('teams_ids_visibilities').value,
              locationsIdsVisibilities: form.get('locations_ids_visibilities').value,
            })
          )
        })
      })
      currentDate = currentDate.add(1, 'day')
    }
    return unassignedShiftParams
  }

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const unassignedShiftParams = buildParameters()
      trackAddUnassignedEvent({ unassignedShiftParams, isDefaultShift: true })
      const result = await asyncAction(
        () => UnassignedShiftsApi.rpc('bulk_create', { unassigned_shifts: unassignedShiftParams }),
        {
          errorFlash: true,
          successMessage: i18n.t('shift_management.actions.add_unassigned_shifts_success'),
        }
      )

      if (isSuccessful(result)) {
        const { value }: { value: UnassignedShiftPayload[] } = result

        const unassignedShiftsList = value.map((unassignedShift) =>
          UnassignedShiftProp.buildFromProps(unassignedShift)
        )

        dispatchSchedule({ type: 'RESET_VIEW', view })
        dispatchUnassignedShifts({
          type: 'ADD_UNASSIGNED_SHIFTS',
          unassignedShifts: unassignedShiftsList,
        })

        closePortal()

        if (onSuccess) onSuccess()
      }
    },
    {
      errorFlash: false,
      successFlash: false,
    }
  )

  return {
    submitting,
    onSubmit,
  }
}

export { useAddUnassignedDefaultShifts }
